'use client';

import { Avatar } from '@lulalife/design-system';
import type { ReactNode } from 'react';

type SettingsUserCellProps = {
  /**
   * First line. Pass a link when the row navigates — this component deliberately
   * does no routing of its own, so the caller keeps ownership of where a person
   * points.
   */
  name: ReactNode;
  /** Avatar initials. */
  initials: string;
  /** Second line, in the muted tone. */
  email?: ReactNode;
  /** Photo to use instead of initials. Blank or absent falls back to initials. */
  photoUrl?: string | null;
  /** Alt text for the photo — the person's name as plain text. */
  photoAlt?: string;
  className?: string;
};

/**
 * The identity cell of a settings table: an avatar beside a name over an email —
 * the prototype's `.cell-user`.
 *
 * The avatar is DS `Avatar`, so its size and shape are the design system's. `sm` is the size
 * the prototype's table draws. Every initial avatar takes the DS default (brand blue), as the
 * Users frame draws them: the old per-person colour had no meaning and made the list look noisy.
 */
export const SettingsUserCell = ({
  name,
  initials,
  email,
  photoUrl,
  photoAlt,
  className,
}: SettingsUserCellProps) => (
  <div
    data-lula-ds=""
    className={`flex min-w-0 items-center gap-space-12 font-sans${className ? ` ${className}` : ''}`}
  >
    <div className="flex shrink-0">
      {photoUrl ? (
        <Avatar type="photo" size="sm" src={photoUrl} alt={photoAlt ?? ''} />
      ) : (
        <Avatar type="initial" size="sm" initials={initials} />
      )}
    </div>
    <div className="flex min-w-0 flex-col">
      {/* `mb-0!`: the app's global span rule adds a bottom margin that would stretch the row. The
          email's 16px line keeps avatar-height content, so the row measures Figma's 60px. A long
          name or email stops at the column's edge, with the whole email on hover. */}
      <span className="typography-label-md-strong mb-0! min-w-0 truncate text-text-primary">
        {name}
      </span>
      {email && (
        <span
          className="typography-body-sm mb-0! min-w-0 truncate text-xs! leading-4! text-text-secondary"
          title={typeof email === 'string' ? email : undefined}
        >
          {email}
        </span>
      )}
    </div>
  </div>
);

export default SettingsUserCell;
